/**
 * WorkloadView
 *
 * Staff-centric view showing workload distribution across agency team.
 * Redesigned for agency owners who need decision signals:
 *   1. Who's overloaded vs who has capacity? (rebalance signal)
 *   2. Where is risk concentrated? (which lead owns most of the fires?)
 *   3. Is anyone unassigned? (gap signal)
 *
 * New sections:
 * - Team pulse header (kept)
 * - TeamActionQueue: aggregate drafts · batches · stale · gaps
 * - ConcentrationAlerts: conditional, surfaces risk + gap + overload clusters
 * - UnassignedBrands: conditional, brands without a lead
 * - StaffRow: enhanced with utilization bar, pending/batch micro-signals,
 *             "Can take more" chip, and per-brand ⇄ Reassign (real mutation)
 */

import { usePortfolioLocks } from '@/hooks/usePortfolioLocks';
import { useGatingStore } from '@/stores/gating.store';
import { LockBadge } from '@/components/gating/LockBadge';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { agencyRoleLabelKey } from '@/utils/agencyRole';
import { tierLabel, lifecycleStageLabel, type LabelT } from '@/utils/portfolioLabels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SelectChevron } from '@/components/ui/Select';
import { Tooltip } from '@/components/ui/Tooltip';
import { showToast } from '@/components/ui/Toast';
import * as agencyService from '@/services/agency.service';
import { GlossaryTooltip } from './GlossaryTooltip';
import {
  type AgencyStaffMember,
  type PortfolioClient,
  type HealthTier,
} from '@/data/portfolio.types';
import { useAgencyStaff } from '@/hooks/usePortfolio';
import { avatarTextClass } from '@/utils/avatarContrast';
import { isLowPipeline } from '@/utils/lowPipeline';

// ── LeadMetrics ───────────────────────────────────────────────────────────────

/** Brand-lead assignment locked by the agency package: compact control with a lock badge, click opens the upsell. */
const LockedAssignButton = ({ label, compact = false }: { label: string; compact?: boolean }) => {
  const { t } = useTranslation('agency');
  const openUpsell = useGatingStore((state) => state.openUpsell);
  return (
    <button
      type="button"
      data-testid="locked-entry-portfolio_brand_lead"
      className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 transition-colors shrink-0 border border-transparent focus:outline-none focus-visible:border-primary-600 rounded px-1"
      onClick={(e) => {
        e.stopPropagation();
        openUpsell({ feature: 'portfolio_brand_lead', upsellPlanSlug: null }, true);
      }}
      aria-label={t('common:gating_locked_label', { feature: label })}
      title={label}
    >
      {compact ? <span aria-hidden="true">⇄</span> : label}
      <LockBadge />
    </button>
  );
};

interface LeadMetrics {
  brandCount: number;
  /** Sum of pendingCount — UNSCHEDULED drafts (NOT approval-status "pending").
   *  Backend `pendingCount` = `statusCounts.get('UNSCHEDULED')`, so the label
   *  in the UI must say "drafts", never "approvals". */
  draftsCount: number;
  /** Sum of pipeline.changesRequested, EXCLUDING Carte Blanche brands — CB
   *  teams have no approval workflow, so they can never contribute to this. */
  changesRequested: number;
  staleItems: number;           // sum of stalePendingCount
  lowPipeline: number;          // count of brands with a low pipeline (server SSOT)
  schedulingGaps: number;       // count of brands with runwayDays === 0 (excl. paused)
  atRiskBrands: number;         // count at_risk + off_track
  utilizationPct: number;       // 0-120 (allows visual overloaded >100)
  capacityLevel: 'healthy' | 'stretched' | 'overloaded';
}

function weightForTier(tier: HealthTier): number {
  switch (tier) {
    case 'on_track': return 1;
    case 'watch':    return 1.2;
    case 'at_risk':  return 1.5;
    case 'off_track': return 2;
    case 'paused':   return 0.3;
  }
}

function computeLeadMetrics(clients: PortfolioClient[]): LeadMetrics {
  const brandCount       = clients.length;
  const draftsCount      = clients.reduce((a, c) => a + c.pendingCount, 0);
  // Carte Blanche brands have no approval workflow — never let them
  // contribute to an approval-specific aggregate like "changes requested".
  const changesRequested = clients
    .filter((c) => !c.pipeline.carteBlanche)
    .reduce((a, c) => a + c.pipeline.changesRequested, 0);
  const staleItems       = clients.reduce((a, c) => a + (c.stalePendingCount ?? 0), 0);
  const lowPipeline      = clients.filter(isLowPipeline).length;
  const schedulingGaps   = clients.filter(
    (c) => c.runwayDays === 0 && c.healthOverall !== 'paused',
  ).length;
  const atRiskBrands     = clients.filter(
    (c) => c.healthOverall === 'at_risk' || c.healthOverall === 'off_track',
  ).length;

  // Weighted workload score. Threshold: 10 = 100% capacity.
  const weightedScore  = clients.reduce((a, c) => a + weightForTier(c.healthOverall), 0);
  const utilizationPct = Math.min((weightedScore / 10) * 100, 120);

  const capacityLevel: LeadMetrics['capacityLevel'] =
    utilizationPct < 50  ? 'healthy'    :
    utilizationPct < 85  ? 'stretched'  :
    'overloaded';

  return {
    brandCount, draftsCount, changesRequested, staleItems,
    lowPipeline, schedulingGaps, atRiskBrands, utilizationPct, capacityLevel,
  };
}

// ── Capacity config ───────────────────────────────────────────────────────────

type CapacityLevel = LeadMetrics['capacityLevel'];

/** Localized capacity label (static keys so the i18n audit can see them). */
const capacityLabel = (t: LabelT, level: CapacityLevel): string => {
  switch (level) {
    case 'stretched': return t('portfolio_workload_capacity_stretched', { defaultValue: 'Stretched' });
    case 'overloaded': return t('portfolio_workload_capacity_overloaded', { defaultValue: 'Overloaded' });
    default: return t('portfolio_workload_capacity_healthy', { defaultValue: 'Healthy' });
  }
};

const CAPACITY_CONFIG: Record<
  CapacityLevel,
  { bg: string; text: string; tooltipKey: string; glossaryTermId: string }
> = {
  healthy:    { bg: 'bg-primary-50', text: 'text-primary-700',  tooltipKey: 'workload_tooltip_capacity_healthy',    glossaryTermId: 'capacity-healthy'    },
  stretched:  { bg: 'bg-peach/30',  text: 'text-coral-700',   tooltipKey: 'workload_tooltip_capacity_stretched',  glossaryTermId: 'capacity-stretched'  },
  overloaded: { bg: 'bg-coral-50',   text: 'text-coral-700',    tooltipKey: 'workload_tooltip_capacity_overloaded', glossaryTermId: 'capacity-overloaded' },
};

// ── Health distribution bar ───────────────────────────────────────────────────

const HEALTH_COLORS: Record<HealthTier, string> = {
  on_track:  'bg-primary-400',
  watch:     'bg-peach',
  at_risk:   'bg-coral-300',
  off_track: 'bg-coral-600',
  paused:    'bg-gray-300',
};

const HEALTH_TIER_ORDER: HealthTier[] = ['on_track', 'watch', 'at_risk', 'off_track', 'paused'];

const HealthDistBar = ({ clients }: { clients: PortfolioClient[] }) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  if (clients.length === 0 || isLocked('portfolio_health_score')) return null;

  const counts: Record<HealthTier, number> = {
    on_track: 0, watch: 0, at_risk: 0, off_track: 0, paused: 0,
  };
  clients.forEach((c) => { counts[c.healthOverall]++; });
  const total = clients.length;

  return (
    <div
      className="flex h-2 rounded-full overflow-hidden gap-[2px]"
      style={{ width: 100 }}
      role="img"
      aria-label={t('workload_health_dist_aria')}
    >
      {HEALTH_TIER_ORDER.map((tier) => {
        const count = counts[tier];
        if (count === 0) return null;
        const pct = (count / total) * 100;
        return (
          <div
            key={tier}
            className={`${HEALTH_COLORS[tier]} h-full rounded-full shrink-0 transition-all`}
            style={{ width: `${pct}%` }}
            title={`${tierLabel(t, tier)}: ${count}`}
          />
        );
      })}
    </div>
  );
};

// ── TeamActionQueue ───────────────────────────────────────────────────────────
// Icons used (all verified in happyicon.css):
//   hm-pen-pencil    (drafts — matches ICONS.DRAFT, see StaffRow)
//   hm-folder        (low pipeline)
//   hm-stop-watch    (stale items)
//   hm-calendar      (schedule gaps)

interface ActionChip {
  count: number;
  label: string;
  icon: string;
  bg: string;
  text: string;
}

const TeamActionQueue = ({ clients }: { clients: PortfolioClient[] }) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const runwayLocked = isLocked('portfolio_runway');

  const draftsCount = clients.reduce((a, c) => a + c.pendingCount, 0);
  const lowPipelineBrands = clients.filter(isLowPipeline).length;
  const stale   = clients.reduce((a, c) => a + (c.stalePendingCount ?? 0), 0);
  const gaps    = clients.filter(
    (c) => c.runwayDays === 0 && c.healthOverall !== 'paused',
  ).length;

  const chips: ActionChip[] = [
    { count: draftsCount, label: t('workload_queue_drafts',  { defaultValue: 'drafts' }),     icon: 'hm-pen-pencil',  bg: 'bg-peach/30',  text: 'text-coral-700'  },
    { count: lowPipelineBrands, label: t('workload_queue_low_pipeline',  { defaultValue: 'low pipeline' }),   icon: 'hm-folder',      bg: 'bg-peach/30', text: 'text-coral-700' },
    { count: stale,   label: t('workload_queue_stale',    { defaultValue: 'stale' }),         icon: 'hm-stop-watch',  bg: 'bg-coral-50',  text: 'text-coral-700'  },
    { count: gaps,    label: t('workload_queue_gaps',     { defaultValue: 'nothing scheduled' }), icon: 'hm-calendar',    bg: 'bg-peach/40',  text: 'text-coral-800'  },
  ];

  // Runway locked: the low-pipeline and "nothing scheduled" chips are runway readings
  const visibleChips = runwayLocked ? chips.filter((c) => c.icon !== 'hm-folder' && c.icon !== 'hm-calendar') : chips;
  const hasAny = visibleChips.some((c) => c.count > 0);
  if (!hasAny) return null;

  return (
    <div className="border border-gray-900/8 rounded-xl px-4 py-3 bg-white">
      <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium mb-2">
        {t('workload_team_queue_title', { defaultValue: 'Team queue - this week' })}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        {visibleChips.map((chip) =>
          chip.count > 0 ? (
            <span
              key={chip.label}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${chip.bg} ${chip.text}`}
            >
              <i className={`hm ${chip.icon}`} style={{ fontSize: 12 }} aria-hidden="true" />
              <strong className="tabular-nums">{chip.count}</strong>
              {chip.label}
            </span>
          ) : null
        )}
      </div>
    </div>
  );
};

// ── ConcentrationAlerts ───────────────────────────────────────────────────────
// Icon used: hm-bell (hm-warning not found in CSS)

interface StaffWithClients {
  staff: AgencyStaffMember;
  clients: PortfolioClient[];
}

const ConcentrationAlerts = ({
  staffWithClients,
  allClients,
}: {
  staffWithClients: StaffWithClients[];
  allClients: PortfolioClient[];
}) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const alerts: string[] = [];

  // Risk concentration: any lead owns >=50% of at-risk brands (and >=2 at-risk total)
  const atRiskAll = allClients.filter(
    (c) => c.healthOverall === 'at_risk' || c.healthOverall === 'off_track',
  );
  if (atRiskAll.length >= 2) {
    staffWithClients.forEach(({ staff, clients }) => {
      const atRisk = clients.filter(
        (c) => c.healthOverall === 'at_risk' || c.healthOverall === 'off_track',
      ).length;
      if (atRisk >= 2 && atRisk / atRiskAll.length >= 0.5) {
        alerts.push(
          t('workload_concentration_risk', {
            defaultValue: '{{name}} manages {{owned}} of {{total}} at-risk brands',
            name: staff.name,
            owned: atRisk,
            total: atRiskAll.length,
          }),
        );
      }
    });
  }

  // Schedule gap concentration: lead owns >=2 brands with scheduling gaps
  const gapAll = allClients.filter(
    (c) => c.runwayDays === 0 && c.healthOverall !== 'paused',
  );
  if (!isLocked('portfolio_runway') && gapAll.length >= 2) {
    staffWithClients.forEach(({ staff, clients }) => {
      const gaps = clients.filter(
        (c) => c.runwayDays === 0 && c.healthOverall !== 'paused',
      ).length;
      if (gaps >= 2) {
        alerts.push(
          t('workload_concentration_gaps', {
            defaultValue: '{{name}} owns {{owned}} brands with scheduling gaps',
            name: staff.name,
            owned: gaps,
          }),
        );
      }
    });
  }

  // Overload concentration
  staffWithClients.forEach(({ staff, clients }) => {
    const m = computeLeadMetrics(clients);
    if (m.capacityLevel === 'overloaded') {
      alerts.push(
        t('workload_concentration_overload', {
          defaultValue: '{{name}} is at {{pct}}% capacity - consider redistributing',
          name: staff.name,
          pct: Math.round(m.utilizationPct),
        }),
      );
    }
  });

  if (alerts.length === 0) return null;

  return (
    <div className="border border-coral-200 bg-coral-50 rounded-xl px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.2em] text-coral-700 font-semibold mb-2 flex items-center gap-1.5">
        <i className="hm hm-bell" style={{ fontSize: 12 }} aria-hidden="true" />
        {t('workload_concentration_title', { defaultValue: 'Concentration alerts' })}
      </p>
      <ul className="space-y-1">
        {alerts.map((alert, i) => (
          <li key={i} className="text-xs text-coral-800 leading-snug">
            • {alert}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── UnassignedBrands ──────────────────────────────────────────────────────────

const UnassignedBrandRow = ({
  client,
  agencyStaff,
}: {
  client: PortfolioClient;
  agencyStaff: AgencyStaffMember[];
}) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const assignMutation = useMutation({
    mutationFn: (newOwnerId: number) =>
      agencyService.updateWorkspace(client.id, { ownerUserId: newOwnerId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agency', 'portfolio'] });
      showToast(`Assigned ${client.name}`, 'success');
      setPickerOpen(false);
    },
    onError: (err: Error) => {
      showToast(`Assign failed: ${err.message}`, 'error');
    },
  });

  return (
    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white border border-peach/60 relative">
      <div
        className={`w-6 h-6 rounded-md overflow-hidden flex items-center justify-center shrink-0 ${client.logoUrl ? '' : client.avatarColor}`}
        aria-hidden="true"
      >
        {client.logoUrl ? (
          <img
            src={client.logoUrl}
            alt=""
            className="w-full h-full object-cover rounded-md"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <span className={`text-[11px] font-bold ${avatarTextClass(client.avatarColor)}`}>{client.avatarInitial}</span>
        )}
      </div>
      <span className="text-xs font-medium text-gray-800 flex-1 truncate">{client.name}</span>
      {isLocked('portfolio_brand_lead') && <LockedAssignButton label={t('workload_unassigned_assign', { defaultValue: 'Assign' })} />}
      {!isLocked('portfolio_brand_lead') && (
      <div ref={pickerRef} className="relative">
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-coral-700 hover:text-coral-800 transition-colors shrink-0 disabled:opacity-50 border border-transparent focus:outline-none focus-visible:border-primary-600 rounded px-1"
          onClick={() => setPickerOpen((v) => !v)}
          aria-expanded={pickerOpen}
          disabled={assignMutation.isPending}
        >
          {assignMutation.isPending ? (
            '…'
          ) : (
            <>
              {t('workload_unassigned_assign', { defaultValue: 'Assign' })}
              <SelectChevron className="shrink-0" />
            </>
          )}
        </button>
        {pickerOpen && (
          <div className="absolute right-0 top-full mt-1 z-30 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 overflow-hidden">
            <p className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
              {t('workload_assign_to', { defaultValue: 'Assign to' })}
            </p>
            {agencyStaff.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400 italic">
                {t('workload_no_staff_available', { defaultValue: 'No team members available' })}
              </p>
            )}
            {agencyStaff.map((staff) => (
              <button
                key={staff.id}
                type="button"
                className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                onClick={() => assignMutation.mutate(staff.id)}
              >
                <span className="w-5 h-5 rounded-full overflow-hidden inline-flex items-center justify-center shrink-0">
                  {staff.profilePhotoUrl ? (
                    <img
                      src={staff.profilePhotoUrl}
                      alt=""
                      className="w-full h-full rounded-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className={`w-full h-full rounded-full inline-flex items-center justify-center text-[9px] font-bold ${avatarTextClass(staff.avatarColor)} ${staff.avatarColor}`}>
                      <span className="translate-y-px">{staff.initials}</span>
                    </span>
                  )}
                </span>
                <span className="truncate">{staff.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

const UnassignedBrands = ({
  clients,
  agencyStaff,
}: {
  clients: PortfolioClient[];
  agencyStaff: AgencyStaffMember[];
}) => {
  const { t } = useTranslation('agency');

  const unassigned = clients.filter(
    (c) => !c.ownerId || c.ownerId === 0 || !agencyStaff.some((s) => s.id === c.ownerId),
  );

  if (unassigned.length === 0) return null;

  return (
    <div className="border border-dashed border-peach bg-peach/30/50 rounded-xl px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.2em] text-coral-700 font-semibold mb-2">
        {t('workload_unassigned_title', { defaultValue: 'Unassigned brands' })} · {unassigned.length}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {unassigned.map((client) => (
          <UnassignedBrandRow key={client.id} client={client} agencyStaff={agencyStaff} />
        ))}
      </div>
    </div>
  );
};

// ── ClientMiniCard ────────────────────────────────────────────────────────────

const TIER_COLOR: Record<HealthTier, string> = {
  on_track:  'text-primary-700 bg-primary-50',
  watch:     'text-coral-700 bg-peach/30',
  at_risk:   'text-coral-700 bg-coral-50',
  off_track: 'text-coral-700 bg-coral-50',
  paused:    'text-sage-gray bg-gray-100',
};

const ClientMiniCard = ({
  client,
  agencyStaff,
}: {
  client: PortfolioClient;
  agencyStaff: AgencyStaffMember[];
}) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const reassignMutation = useMutation({
    mutationFn: (newOwnerId: number) =>
      agencyService.updateWorkspace(client.id, { ownerUserId: newOwnerId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agency', 'portfolio'] });
      showToast(`Reassigned ${client.name}`, 'success');
      setPickerOpen(false);
    },
    onError: (err: Error) => {
      showToast(`Reassign failed: ${err.message}`, 'error');
    },
  });

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 group relative">
      <div
        className={`w-6 h-6 rounded-md overflow-hidden flex items-center justify-center shrink-0 ${client.logoUrl ? '' : client.avatarColor}`}
        aria-hidden="true"
      >
        {client.logoUrl ? (
          <img
            src={client.logoUrl}
            alt=""
            className="w-full h-full object-cover rounded-md"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <span className={`text-[11px] font-bold ${avatarTextClass(client.avatarColor)}`}>{client.avatarInitial}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-800 truncate">{client.name}</p>
        <p className="text-[10px] text-gray-400">{lifecycleStageLabel(t, client.lifecycleStage)}</p>
      </div>
      {!isLocked('portfolio_health_score') && (
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${TIER_COLOR[client.healthOverall]}`}>
          {tierLabel(t, client.healthOverall)}
        </span>
      )}
      {isLocked('portfolio_brand_lead') && <LockedAssignButton label={t('workload_reassign_title')} compact />}
      {!isLocked('portfolio_brand_lead') && (
      <div ref={pickerRef} className="relative">
        <button
          type="button"
          className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 focus:opacity-100 transition-opacity inline-flex [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] items-center justify-center text-[10px] font-medium text-primary-600 hover:text-primary-700 shrink-0 ml-1 border border-transparent rounded focus:outline-none focus-visible:border-primary-600"
          onClick={(e) => {
            e.stopPropagation();
            setPickerOpen((v) => !v);
          }}
          title={t('workload_reassign_title')}
          aria-label={t('workload_reassign_aria', { name: client.name })}
          aria-expanded={pickerOpen}
          disabled={reassignMutation.isPending}
        >
          {reassignMutation.isPending ? '…' : (
            <>
              <span aria-hidden="true">⇄</span>
              <SelectChevron className="ml-0.5 shrink-0" />
            </>
          )}
        </button>
        {pickerOpen && (
          <div className="absolute right-0 top-full mt-1 z-30 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 overflow-hidden">
            <p className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
              {t('workload_reassign_to')}
            </p>
            {agencyStaff
              .filter((s) => s.id !== client.ownerId)
              .map((staff) => (
                <button
                  key={staff.id}
                  type="button"
                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    reassignMutation.mutate(staff.id);
                  }}
                >
                  <span className="w-5 h-5 rounded-full overflow-hidden inline-flex items-center justify-center shrink-0">
                    {staff.profilePhotoUrl ? (
                      <img
                        src={staff.profilePhotoUrl}
                        alt=""
                        className="w-full h-full rounded-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span className={`w-full h-full rounded-full inline-flex items-center justify-center text-[9px] font-bold ${avatarTextClass(staff.avatarColor)} ${staff.avatarColor}`}>
                        <span className="translate-y-px">{staff.initials}</span>
                      </span>
                    )}
                  </span>
                  <span className="truncate">{staff.name}</span>
                </button>
              ))}
            {agencyStaff.filter((s) => s.id !== client.ownerId).length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">{t('workload_no_other_staff')}</p>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

// ── StaffRow ──────────────────────────────────────────────────────────────────
// Icons used (all verified in happyicon.css):
//   hm-pen-pencil  (drafts inline signal — matches ICONS.DRAFT)
//   hm-folder      (low pipeline inline signal)
//   hm-caret-down  (expand chevron)
//   hm-group       (empty state)

interface StaffRowProps {
  staff: AgencyStaffMember;
  clients: PortfolioClient[];
  someoneOverloaded: boolean;
  agencyStaff: AgencyStaffMember[];
}

const StaffRow = ({ staff, clients, someoneOverloaded, agencyStaff }: StaffRowProps) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const healthLocked = isLocked('portfolio_health_score');
  const runwayLocked = isLocked('portfolio_runway');
  const [expanded, setExpanded] = useState(false);

  const metrics  = computeLeadMetrics(clients);
  const capCfg   = CAPACITY_CONFIG[metrics.capacityLevel];

  // Width for utilization bar - cap visual at 100% even if pct can be 120
  const barWidthPct = Math.min(metrics.utilizationPct, 100);

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
      {/* ── Header row ── */}
      <button
        type="button"
        className="w-full text-left px-4 py-3 bg-white hover:bg-gray-50 transition-colors flex items-center gap-3 border border-transparent focus:outline-none focus-visible:border-primary-600"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={t('workload_staff_row_aria', {
          count: clients.length,
          name: staff.name,
          capacity: capacityLabel(t, metrics.capacityLevel),
          action: expanded ? t('workload_row_collapse') : t('workload_row_expand'),
        })}
      >
        {/* Avatar */}
        <div
          className="w-10 h-10 rounded-full overflow-hidden shrink-0 flex items-center justify-center"
          aria-hidden="true"
        >
          {staff.profilePhotoUrl ? (
            <img
              src={staff.profilePhotoUrl}
              alt=""
              className="w-full h-full rounded-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <span
              className={`w-full h-full rounded-full inline-flex items-center justify-center text-sm font-bold ${avatarTextClass(staff.avatarColor)} leading-none tracking-[0.02em] ${staff.avatarColor}`}
            >
              <span className="translate-y-px">{staff.initials}</span>
            </span>
          )}
        </div>

        {/* Name + role */}
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-gray-900">{staff.name}</p>
          <p className="text-xs text-gray-500">{t(agencyRoleLabelKey(staff.role))}</p>
        </div>

        {/* Brand count */}
        <div className="text-right shrink-0 hidden sm:block">
          <p className="text-sm font-bold text-gray-700 tabular-nums">{clients.length}</p>
          <p className="text-xs text-gray-400">{t('portfolio_workload_clients')}</p>
        </div>

        {/* Health distribution bar */}
        {clients.length > 0 && (
          <Tooltip
            content={t('workload_tooltip_health_bar', {
              defaultValue: "Breakdown of this lead's clients by health tier.",
            })}
            side="top"
            delayDuration={300}
          >
            <div className="shrink-0 hidden sm:block cursor-default">
              <HealthDistBar clients={clients} />
            </div>
          </Tooltip>
        )}

        {/* Brand avatar stack - collapsed only */}
        {clients.length > 0 && !expanded && (
          <div className="hidden md:flex items-center -space-x-1.5 shrink-0 mr-1">
            {clients.slice(0, 5).map((c) => (
              <div
                key={c.id}
                className={`w-6 h-6 rounded-md overflow-hidden flex items-center justify-center ring-2 ring-white ${c.logoUrl ? '' : c.avatarColor}`}
                title={c.name}
              >
                {c.logoUrl ? (
                  <img
                    src={c.logoUrl}
                    alt=""
                    className="w-full h-full object-cover rounded-md"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <span className={`text-[10px] font-bold ${avatarTextClass(c.avatarColor)}`}>{c.avatarInitial}</span>
                )}
              </div>
            ))}
            {clients.length > 5 && (
              <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-semibold text-gray-600 bg-gray-100 ring-2 ring-white">
                +{clients.length - 5}
              </div>
            )}
          </div>
        )}

        {/* Action queue micro-signals (desktop only) */}
        {(metrics.draftsCount > 0 || (!runwayLocked && metrics.lowPipeline > 0)) && (
          <div className="hidden lg:flex items-center gap-2 shrink-0 text-[11px] text-gray-500">
            {metrics.draftsCount > 0 && (
              <span
                className="inline-flex items-center gap-1"
                title={t('workload_drafts_title', { count: metrics.draftsCount })}
              >
                <i className="hm hm-pen-pencil" style={{ fontSize: 11 }} aria-hidden="true" />
                <span className="tabular-nums">{metrics.draftsCount}</span>
              </span>
            )}
            {!runwayLocked && metrics.lowPipeline > 0 && (
              <span
                className="inline-flex items-center gap-1 text-coral-700"
                title={`${metrics.lowPipeline} ${t('workload_queue_low_pipeline', { defaultValue: 'low pipeline' })}`}
              >
                <i className="hm hm-folder" style={{ fontSize: 11 }} aria-hidden="true" />
                <span className="tabular-nums">{metrics.lowPipeline}</span>
              </span>
            )}
          </div>
        )}

        {/* Utilization meter + capacity pill + "Can take more" chip */}
        <div className="shrink-0 flex flex-col items-end gap-1 min-w-[72px] sm:min-w-[92px]">
          {/* Bar: desktop only (too narrow for mobile) */}
          <div className="relative h-1.5 w-16 sm:w-20 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`absolute top-0 left-0 h-full transition-all ${
                metrics.capacityLevel === 'healthy'   ? 'bg-primary-400' :
                metrics.capacityLevel === 'stretched' ? 'bg-peach'  : 'bg-coral-500'
              }`}
              style={{ width: `${barWidthPct}%` }}
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-end">
            <GlossaryTooltip termId={capCfg.glossaryTermId} side="top" frameShape="pill">
              <span
                className={`text-[11px] font-medium px-2 py-0.5 rounded-full cursor-default ${capCfg.bg} ${capCfg.text}`}
              >
                {capacityLabel(t, metrics.capacityLevel)}
              </span>
            </GlossaryTooltip>
            {metrics.capacityLevel === 'healthy' && someoneOverloaded && (
              <span className="hidden sm:inline text-[9px] uppercase tracking-[0.1em] font-bold text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded">
                {t('workload_can_take', { defaultValue: 'Can take more' })}
              </span>
            )}
          </div>
        </div>

        {/* Expand chevron */}
        <Tooltip
          content={t('workload_tooltip_expand', {
            defaultValue: 'Show all clients assigned to this lead.',
          })}
          side="left"
          delayDuration={300}
          disabled={expanded}
        >
          <span className="w-6 h-6 flex items-center justify-center shrink-0 self-center">
            <SelectChevron
              className={`text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            />
          </span>
        </Tooltip>
      </button>

      {/* ── Expanded client list ── */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${expanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}
        aria-hidden={!expanded}
      >
        <div className="px-4 pb-4 pt-2 bg-gray-50 border-t border-gray-100">
          {clients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <i className="hm hm-group text-gray-300 mb-2" style={{ fontSize: 32 }} aria-hidden="true" />
              <p className="text-xs text-gray-500">{t('portfolio_workload_no_clients')}</p>
            </div>
          ) : (
            <>
              {/* Per-lead queue summary */}
              {(metrics.draftsCount > 0 || metrics.staleItems > 0 || (!runwayLocked && (metrics.lowPipeline > 0 || metrics.schedulingGaps > 0))) && (
                <div className="flex flex-wrap items-center gap-2 pb-3 pt-1">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium mr-1">
                    {t('workload_breakdown_queue', { defaultValue: 'Queue' })}
                  </span>
                  {metrics.draftsCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-coral-700 bg-peach/30 px-2 py-0.5 rounded-full font-medium">
                      <i className="hm hm-pen-pencil" style={{ fontSize: 10 }} aria-hidden="true" />
                      <span className="tabular-nums">{metrics.draftsCount}</span> {t('workload_queue_drafts', { defaultValue: 'drafts' })}
                    </span>
                  )}
                  {!runwayLocked && metrics.lowPipeline > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-coral-700 bg-peach/30 px-2 py-0.5 rounded-full font-medium">
                      <i className="hm hm-folder" style={{ fontSize: 10 }} aria-hidden="true" />
                      <span className="tabular-nums">{metrics.lowPipeline}</span> {t('workload_queue_low_pipeline', { defaultValue: 'low pipeline' })}
                    </span>
                  )}
                  {metrics.staleItems > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-coral-700 bg-coral-50 px-2 py-0.5 rounded-full font-medium">
                      <i className="hm hm-stop-watch" style={{ fontSize: 10 }} aria-hidden="true" />
                      <span className="tabular-nums">{metrics.staleItems}</span> {t('workload_queue_stale', { defaultValue: 'stale' })}
                    </span>
                  )}
                  {!runwayLocked && metrics.schedulingGaps > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-coral-800 bg-peach/40 px-2 py-0.5 rounded-full font-medium">
                      <i className="hm hm-calendar" style={{ fontSize: 10 }} aria-hidden="true" />
                      <span className="tabular-nums">{metrics.schedulingGaps}</span> {t('workload_queue_gaps', { defaultValue: 'nothing scheduled' })}
                    </span>
                  )}
                </div>
              )}

              {/* Client health breakdown header */}
              <div className="flex items-center gap-3 pb-3 pt-1 text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
                {!healthLocked && <span>{t('workload_breakdown_title', { defaultValue: 'Client health' })}</span>}
                <HealthDistBar clients={clients} />
                <span className="ml-auto tabular-nums text-gray-500 normal-case tracking-normal">
                  {clients.length}{' '}
                  {t('workload_client', { count: clients.length, defaultValue_one: 'client', defaultValue_other: 'clients' })}
                </span>
              </div>

              {/* Client mini-cards with hover ⇄ reassign action */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {clients.map((client) => (
                  <ClientMiniCard key={client.id} client={client} agencyStaff={agencyStaff} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface WorkloadViewProps {
  filteredClients: PortfolioClient[];
  isLoading: boolean;
}

export const WorkloadView = ({ filteredClients: rawClients, isLoading }: WorkloadViewProps) => {
  const { t } = useTranslation('agency');
  const { data: agencyStaff = [] } = useAgencyStaff();
  const { isLocked } = usePortfolioLocks();
  const healthLocked = isLocked('portfolio_health_score');
  // Health locked: every tier-derived aggregate (at-risk counts, weights, alerts) must not reveal the tier,
  // so non-paused brands read as one neutral tier here
  const filteredClients = useMemo(
    () =>
      healthLocked
        ? rawClients.map((c) =>
            c.healthOverall === 'paused' || c.healthOverall === 'on_track' ? c : { ...c, healthOverall: 'on_track' as const },
          )
        : rawClients,
    [healthLocked, rawClients],
  );

  const staffWithClients: StaffWithClients[] = agencyStaff.map((staff) => ({
    staff,
    clients: filteredClients.filter((c) => c.ownerId === staff.id),
  }));

  // Compute metrics once for concentration + can-take signals
  const staffMetrics = staffWithClients.map(({ staff, clients }) => ({
    staff,
    clients,
    metrics: computeLeadMetrics(clients),
  }));

  const anyOverloaded = staffMetrics.some((s) => s.metrics.capacityLevel === 'overloaded');

  // Team pulse header counts using weighted metrics
  const headerCounts: Record<CapacityLevel, number> = { healthy: 0, stretched: 0, overloaded: 0 };
  staffMetrics.forEach(({ metrics }) => { headerCounts[metrics.capacityLevel]++; });

  const totalAssigned = staffWithClients.reduce((acc, s) => acc + s.clients.length, 0);

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 py-4 space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-4 space-y-3">
      {/* ── Team pulse header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-3 px-1">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500 leading-tight">
            {t('workload_overline', { defaultValue: 'Team capacity' })}
          </p>
          <h2 className="font-wagner text-3xl leading-none tabular-nums text-gray-900 mt-1">
            {totalAssigned}
            <span className="text-base text-gray-400 font-medium ml-2">
              {t('workload_brands_assigned', { defaultValue: 'brands assigned' })}
            </span>
          </h2>
        </div>
        <div className="flex items-center gap-2 text-[11px] flex-wrap">
          {(['healthy', 'stretched', 'overloaded'] as const).map((lvl) => {
            const count = headerCounts[lvl];
            const c = CAPACITY_CONFIG[lvl];
            return (
              <span
                key={lvl}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}
              >
                <strong className="tabular-nums">{count}</strong> {capacityLabel(t, lvl).toLowerCase()}
              </span>
            );
          })}
        </div>
      </div>

      {/* ── Team action queue ── */}
      <TeamActionQueue clients={filteredClients} />

      {/* ── Concentration alerts ── */}
      <ConcentrationAlerts
        staffWithClients={staffWithClients}
        allClients={filteredClients}
      />

      {/* ── Unassigned brands ── */}
      <UnassignedBrands clients={filteredClients} agencyStaff={agencyStaff} />

      {/* ── Lead rows ── */}
      {staffWithClients.map(({ staff, clients }) => (
        <StaffRow
          key={staff.id}
          staff={staff}
          clients={clients}
          someoneOverloaded={anyOverloaded}
          agencyStaff={agencyStaff}
        />
      ))}

      {filteredClients.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 md:py-16 text-center">
          <i className="hm hm-group text-gray-300 mb-4" style={{ fontSize: 48 }} aria-hidden="true" />
          <p className="text-base font-semibold text-gray-900 mb-1">
            {t('portfolio_empty_title', { defaultValue: 'No clients match this view' })}
          </p>
          <p className="text-sm text-gray-500 max-w-md">
            {t('portfolio_empty_desc', {
              defaultValue: 'Try adjusting the filters to see more clients.',
            })}
          </p>
        </div>
      )}
    </div>
  );
};
